import * as React from 'react';

import Typography from 'antd/lib/typography';

import { Nullable, Optional } from '@common/typescript/objects/Nullable';

import { ServiceType } from '@app/objects/Pet';
import { PriceKind, PriceType } from '@app/objects/Price';
import { PetPrice } from '@app/objects/PetPrice';
import { ItemsList } from '@app/components/UI/Cards/Card';

interface FiltersParams {
	priceKind: Array<PriceKind>;
}

export interface PriceListParams {
	clinicId: Nullable<number>;
	priceType: PriceType;
	serviceType: ServiceType;
	withInventoryItem: boolean;
	count: number;
	filters: FiltersParams;
}

export function getPetPrice(items: Array<PetPrice>): Array<ItemsList> {
	return items.map((elm: PetPrice) => ({
		id: elm.id,
		title: elm.note ? `${elm.name} - ${elm.note}` : elm.name,
		data: elm.count.toString(),
		content: elm.parent ? <Typography.Text type="secondary">{elm.parent.name}</Typography.Text> : null,
	}));
}

type PriceValue = Pick<PetPrice, 'batchPrice' | 'batchCount' | 'value' | 'parentId'>;
interface CalculatedPriceValue {
	value: number;
	extra: number;
}

export function getPriceValue(price: PriceValue, count: number = 1): CalculatedPriceValue {
	if (price.parentId) return { value: 0, extra: 0 };

	const batchCount: number = price.batchCount ?? 0;
	const batchPrice: number = price.batchPrice ?? 0;
	const perItem: number = price.value ?? 0;

	const extra = Math.max(count - batchCount, 0) * perItem;
	const value = batchPrice + extra;

	return {
		value,
		extra,
	};
}

/**
 * Calculate over batch price
 * @param {Array<PetPrice>} - a list of requested services or urns for current pet
 * @returns {number} - total additional price
 */
export function getAdditionalPrice(array: Array<PetPrice> = []): number {
	return array.reduce((acc: number, elm: PetPrice) => {
		let price: Optional<PriceValue> = elm.price;
		if (!price) {
			price = {
				batchCount: elm.batchCount,
				batchPrice: elm.batchPrice,
				value: elm.value,
				parentId: elm.parentId,
			};
		}

		const value = getPriceValue(price, elm.count).value;

		return value + acc;
	}, 0);
}

/**
 * Get disable 'Add'-btn for services & products modal
 * @param {ServiceType} - value of serviceType
 * @returns {boolean}
 */
export const getDisableModal = (serviceType?: ServiceType): boolean => (serviceType === undefined || serviceType < 0);
